import React from 'react';
import { useIntl } from 'react-intl';
import { MerchantDetail } from '../../../../interfaces/merchants';
import { MerchantBookingPageApiResponse } from '../../../../interfaces/merchants/bookings';
import {
  MerchantProduct,
  MerchantProductStatus,
} from '../../../../interfaces/merchants/products';
import Heading from '../../../../atoms/Heading';
import BaseTemplate from '../../../../organisms/merchants/BaseTemplate';
import ServiceCard from '../../../../molecules/merchant/common/cards/ServiceCard';
import DescriptionPanel from '../../../../molecules/merchant/DescriptionPanel';
import MerchantProfile from '../../../../molecules/merchant/MerchantProfile';
import MerchantProductDetailsHeader from '../../../../organisms/merchants/products/MerchantProductDetailsHeader';
import css from './MerchantProductDetailsPage.module.scss';
import { RSButtonAnchor } from 'src/reservation-page/RSButtonAnchor';
import { buildSiteUrl } from 'src/utils/request-url';

interface Props {
  merchantDetail: MerchantDetail;
  product: MerchantProduct;
  bookingServices: MerchantBookingPageApiResponse[];
  productStatus: string;
  productId: string;
  isCoubicApp: boolean;
}

const toThumbnailUrl = (bookingService: MerchantBookingPageApiResponse) => {
  // image
  if (bookingService.primary_image) {
    return bookingService.primary_image.square!.url;
  }
  // video
  if (bookingService.cover_content) {
    return bookingService.cover_content.thumbnail_url;
  }
  // nothing
  return '';
};

const MerchantProductDetailsPage = ({
  merchantDetail,
  product,
  bookingServices,
  productStatus,
  productId,
  isCoubicApp,
}: Props) => {
  const intl = useIntl();
  if (!product) {
    return null;
  }
  const opened = productStatus === MerchantProductStatus.Accepting;
  return (
    <BaseTemplate merchantDetail={merchantDetail} isCoubicApp={isCoubicApp}>
      <div className={css.MerchantProductDetailsPage}>
        <MerchantProductDetailsHeader
          merchantSetting={merchantDetail.merchant_setting}
          productId={productId}
          opened={opened}
          imageUrl={product.image}
          scheme={product.scheme}
          name={product.name}
          price={product.price}
          ticketCount={product.ticket_count}
          expiration={product.expiration}
        />
        <div className={css.MerchantProductDetailsPage__Body}>
          <DescriptionPanel
            description_text={product.description || ''}
            is_json_description={false}
          />
        </div>
        {bookingServices.length > 0 && (
          <div className={css.MerchantProductDetailsPage__Section}>
            <Heading
              id="product-available-services"
              className={css.MerchantProductDetailsPage__Heading}
            >
              {intl.formatMessage({ id: 'merchants.products.services' })}
            </Heading>
            <div className={css.MerchantProductDetailsPage__ServiceCards}>
              {bookingServices.map((bookingService) => (
                <ServiceCard
                  key={bookingService.public_id}
                  className={css.MerchantProductDetailsPage__ServiceCard}
                  title={bookingService.name}
                  description={bookingService.description_text || ''}
                  pageUrl={buildSiteUrl(
                    `/${bookingService.public_id}#pageContent`,
                  )}
                  thumbnailUrl={toThumbnailUrl(bookingService)}
                />
              ))}
            </div>
          </div>
        )}
        <div className={css.MerchantProductDetailsPage__Section}>
          <Heading className={css.MerchantProductDetailsPage__Heading}>
            {intl.formatMessage(
              { id: 'merchants.details.storeData' },
              { displayName: merchantDetail.display_name },
            )}
          </Heading>
          <MerchantProfile merchantDetail={merchantDetail} />
          <div
            className={css.MerchantProductDetailsPage__BackToTopButtonSection}
          >
            <div className="mx-auto max-w-[370px]">
              <RSButtonAnchor variant="outline" isBlock href={buildSiteUrl()}>
                {intl.formatMessage({
                  id: 'merchants.details.backToMerchantTop',
                })}
              </RSButtonAnchor>
            </div>
          </div>
        </div>
      </div>
    </BaseTemplate>
  );
};

export default MerchantProductDetailsPage;
